import React, { useState, useRef, useEffect } from 'react';
import { useHistory } from "react-router-dom";
import Auth from "../services/AuthService";
import util from "../utils/util";
import { trailLogin, trailApiError, TRAIL_EVENTS, logTrail } from '../utils/activityTrail';
import flagImg from './assets/footer/flag.png';
import { Input, Button, message, Modal } from 'antd';

const FALLBACK_COLORS = ['#0ea5e9','#8b5cf6','#f59e0b','#10b981','#ef4444','#3b82f6','#6366f1','#ec4899','#14b8a6','#a855f7'];

export default function Login() {
    const history = useHistory();
    const isMount = useRef(true);
    const usernameRef = useRef();
    const passwordRef = useRef();

    const [values, setValues]           = useState({});
    const [loading, setLoading]         = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError]             = useState('');
    const [loginAttempts, setLoginAttempts] = useState(() =>
        parseInt(localStorage.getItem('loginAttempts') || '0')
    );
    const [isLocked, setIsLocked]       = useState(false);
    const [lockoutTime, setLockoutTime] = useState(() => {
        const saved = localStorage.getItem('lockoutTime');
        return saved ? parseInt(saved) : null;
    });
    const [rememberMe, setRememberMe]   = useState(false);
    const [capsLockOn, setCapsLockOn]   = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [connectionStatus, setConnectionStatus] = useState('online');
    const [showForgot,  setShowForgot]  = useState(false);
    const [forgotEmail, setForgotEmail] = useState('');
    const [forgotSent,  setForgotSent]  = useState(false);
    const [forgotLoading, setForgotLoading] = useState(false);
    // Dynamic standards+clauses for the login screen, fetched from the public
    // (no-auth) endpoint -- never hardcoded, so new standards/clauses created
    // through Standards Setup show up here automatically.
    const [standardsList, setStandardsList] = useState([]);
    const [clausesLoading, setClausesLoading] = useState(true);

    useEffect(() => {
        // Build the API origin the same way utils/axios.js does -- a relative
        // path here would resolve against the CURRENT origin, which in dev is
        // the React dev server (:3000), not the real API (bare hostname via
        // Apache). That mismatch silently served the SPA shell instead of JSON.
        const apiOrigin = window.location.hostname.includes('qoctesting')
            ? window.location.protocol + '//' + window.location.hostname + '/qoctesting/api/'
            : window.location.protocol + '//' + window.location.hostname + '/qoc/api/';
        fetch(apiOrigin + 'qoc/public/clauses')
            .then(r => r.json())
            .then(res => {
                if (res?.code === 200 && Array.isArray(res.data)) {
                    setStandardsList(res.data);
                }
            })
            .catch(() => { /* silently fall back to generic placeholder below */ })
            .finally(() => setClausesLoading(false));
    }, []);

    // Flatten standards -> clauses into a single ordered list for the display
    // grid, assigning each clause a color (the owning standard's accent, or a
    // cycled fallback color when accent is not set).
    const flatClauses = standardsList.flatMap(std =>
        (std.clauses || []).map(c => ({
            code: c.code,
            title: c.title,
            color: std.accent || FALLBACK_COLORS[(c.clause_number - 1) % FALLBACK_COLORS.length],
        }))
    );
    const totalStandards = standardsList.length;

    useEffect(() => {
        const upd = () => setConnectionStatus(navigator.onLine ? 'online' : 'offline');
        window.addEventListener('online', upd);
        window.addEventListener('offline', upd);
        return () => {
            window.removeEventListener('online', upd);
            window.removeEventListener('offline', upd);
        };
    }, []);

    useEffect(() => {
        const remembered = localStorage.getItem('rememberedUsername');
        if (remembered) { setValues(p => ({ ...p, username: remembered })); setRememberMe(true); }
        const lockout = localStorage.getItem('lockoutTime');
        if (lockout && Date.now() < parseInt(lockout)) {
            setIsLocked(true); setLockoutTime(parseInt(lockout));
        } else if (lockout) {
            localStorage.removeItem('lockoutTime');
            localStorage.removeItem('loginAttempts');
            setLoginAttempts(0);
        }
    }, []);

    useEffect(() => {
        if (isLocked && lockoutTime) {
            const t = setInterval(() => {
                if (Date.now() >= lockoutTime) {
                    setIsLocked(false); setLockoutTime(null); setLoginAttempts(0);
                    localStorage.removeItem('lockoutTime'); localStorage.removeItem('loginAttempts');
                }
            }, 1000);
            return () => clearInterval(t);
        }
    }, [isLocked, lockoutTime]);

    useEffect(() => { usernameRef.current?.focus(); }, []);
    useEffect(() => { return () => { isMount.current = false; }; }, []);

    const getRemainingTime = () => {
        if (!lockoutTime) return '';
        const rem = Math.ceil((lockoutTime - Date.now()) / 1000);
        if (rem <= 0) return '0:00';
        return `${Math.floor(rem/60)}:${(rem%60).toString().padStart(2,'0')}`;
    };

    const validate = () => {
        const e = {};
        if (!values.username?.trim())        e.username = 'Username is required';
        else if (values.username.length < 3) e.username = 'At least 3 characters required';
        if (!values.password)                e.password = 'Password is required';
        else if (values.password.length < 4) e.password = 'At least 4 characters required';
        setValidationErrors(e);
        return Object.keys(e).length === 0;
    };

    const login = async (e) => {
        if (e) e.preventDefault();
        if (connectionStatus === 'offline') { setError('No internet connection.'); return; }
        if (isLocked) { setError(`Locked. Try again in ${getRemainingTime()}.`); return; }
        if (!validate()) return;

        setError(''); setLoading(true);
        try {
            const { data } = await Auth.login(values);
            util.setLoginInfoLocalStorage(
                data.token, data.name, data.modules, data.type,
                data.is_national, data.is_client_admin, data.is_admin, data.client_id,
                data.permissions, data.region, data.nav || [], data.regions,
                data.program_id, data.account_type, data.default_landing_page, data.id
            );
            if (rememberMe) localStorage.setItem('rememberedUsername', values.username);
            setLoginAttempts(0);

            localStorage.removeItem('loginAttempts');
            localStorage.removeItem('lockoutTime');
            trailLogin(data.name);

            // Route to the first module this user actually has access to,
            // instead of hardcoding everyone to the Executive Dashboard.
            // Without this, roles like assessment collectors (qoc_assess only)
            // land on a page they're not permitted to view and get an uncaught
            // "You are not allowed to access this module" crash.
            const m = data.modules || {};
            localStorage.setItem('force_password_change', data.force_password_change || 0);
            const validPaths = (data.nav || []).map(n => n.path);
            const landingRoute = parseInt(data.force_password_change) === 1
                ? '/profile?force=1'
                : (data.default_landing_page && validPaths.includes(data.default_landing_page))
                    ? data.default_landing_page
                    : '/welcome';
            history.push(landingRoute);

        } catch (err) {
            logTrail(TRAIL_EVENTS.ERROR, 'Auth',
                { username: values.username, reason: err.message || 'Invalid credentials' },
                'Login failed'
            );
            const n = loginAttempts + 1;
            setLoginAttempts(n);
            localStorage.setItem('loginAttempts', n.toString());
            if (n >= 5) {
                const lockTime = Date.now() + 300000;
                setIsLocked(true); setLockoutTime(lockTime);
                localStorage.setItem('lockoutTime', lockTime.toString());
                setError('Too many failed attempts. Account locked for 5 minutes.');
            } else {
                setError(`${err.message || 'Invalid credentials'}. ${5-n} attempt${5-n!==1?'s':''} remaining.`);
                passwordRef.current?.focus();
                passwordRef.current?.select();
            }
        } finally {
            if (isMount.current) setLoading(false);
        }
    };

    const handleInputChange = (field, value) => {
        setValues(p => ({ ...p, [field]: value }));
        if (validationErrors[field]) setValidationErrors(p => ({ ...p, [field]: '' }));
        if (error) setError('');
    };

    const handleKeyDown = (e, field) => {
        if (field === 'password') setCapsLockOn(e.getModifierState?.('CapsLock') || false);
        if (e.key === 'Enter') {
            e.preventDefault();
            if (field === 'username') passwordRef.current?.focus();
            else login(e);
        }
    };

    const handleForgot = async () => {
        if (!forgotEmail) { message.error('Please enter your email address'); return; }
        setForgotLoading(true);
        try {
            await Auth.forgotPassword({ email: forgotEmail });
            setForgotSent(true);
        } catch(e) {
            message.error('Failed to send reset email. Please try again.');
        } finally { setForgotLoading(false); }
    };

    const closeForgotModal = () => {
        setShowForgot(false);
        setForgotEmail('');
        setForgotSent(false);
    };

    const styles = `
        @import url('https://fonts.googleapis.com/css2?family=EB+Garamond:wght@400;500;600;700;800&family=Crimson+Text:wght@400;600;700&display=swap');
        * { margin:0; padding:0; box-sizing:border-box; }
        body { font-family:'EB Garamond',serif; }

        .qoc-main {
            min-height:100vh;
            display:flex;
            align-items:stretch;
            position:relative;
            overflow:hidden;
        }

        /* ── LEFT PANEL ────────────────────────────── */
        .qoc-left {
            width:48%;
            min-height:100vh;
            position:relative;
            overflow:hidden;
            background:#071220;
            display:flex;
            flex-direction:column;
        }
        /* Heavy blur + very dark overlay so text pops cleanly */
        .qoc-left-bg {
            position:absolute;
            inset:0;
            background:url('https://images.unsplash.com/photo-1551076805-e1869033e561?w=900&q=80&fit=crop&crop=center') center/cover no-repeat;
            filter:blur(4px) brightness(0.35);
            transform:scale(1.06); /* prevent blur edge bleed */
        }
        /* Extra dark gradient on top of blurred photo */
        .qoc-left-overlay {
            position:absolute;
            inset:0;
            background:linear-gradient(160deg,rgba(7,18,32,0.55) 0%,rgba(7,18,32,0.82) 50%,rgba(7,18,32,0.96) 100%);
        }
        .qoc-left-content {
            position:relative;
            z-index:3;
            flex:1;
            display:flex;
            flex-direction:column;
            padding:2.8rem 3rem;
            min-height:100vh;
        }

        /* TOP — MoH branding + national flag */
        .qoc-left-top {
            display:flex;
            align-items:center;
            justify-content:space-between;
            gap:20px;
        }
        .qoc-moh-label {
            font-size:1rem;
            font-weight:800;
            letter-spacing:4px;
            text-transform:uppercase;
            color:#c8102e;
            margin-bottom:0.3rem;
        }
        .qoc-kingdom {
            font-family:'Crimson Text',serif;
            font-size:1.3rem;
            font-weight:700;
            color:rgba(255,255,255,0.92);
            letter-spacing:1px;
        }
        .qoc-flag {
            width:58px;
            height:auto;
            display:block;
            border-radius:4px;
            border:1.5px solid rgba(201,169,97,0.55);
            box-shadow:0 4px 14px rgba(0,0,0,0.4);
            flex-shrink:0;
        }

        /* MIDDLE — hero text */
        .qoc-left-main {
            display:flex;
            flex-direction:column;
            align-items:center;
            text-align:center;
            margin-top:2.5rem;
            margin-bottom:2.5rem;
        }
        .qoc-left-title {
            font-size:4.4rem;
            font-weight:800;
            color:white;
            margin-bottom:0.6rem;
            text-shadow:0 3px 12px rgba(0,0,0,0.5);
            line-height:1.1;
        }
        .qoc-left-title span { color:#c9a961; }
        .qoc-left-subtitle {
            color:rgba(255,255,255,0.88);
            font-size:1.55rem;
            line-height:1.65;
            margin-bottom:2rem;
            text-shadow:0 1px 4px rgba(0,0,0,0.4);
        }
        .qoc-left-badges {
            display:flex;
            gap:10px;
            flex-wrap:wrap;
            margin-bottom:0;
        }
        .qoc-left-badge {
            background:rgba(255,255,255,0.14);
            backdrop-filter:blur(12px);
            border:1.5px solid rgba(255,255,255,0.3);
            border-radius:24px;
            padding:6px 18px;
            color:white;
            font-size:0.95rem;
            font-weight:700;
            letter-spacing:0.5px;
        }

        /* BOTTOM — clauses panel */
        .qoc-clauses {
            margin-top:1.5rem;
            background:rgba(0,0,0,0.45);
            backdrop-filter:blur(16px);
            border:1px solid rgba(201,169,97,0.35);
            border-radius:12px;
            padding:1.4rem 1.8rem;
        }
        .qoc-clauses-title {
            font-size:0.95rem;
            font-weight:800;
            letter-spacing:3px;
            text-transform:uppercase;
            color:#c9a961;
            margin-bottom:1rem;
            border-bottom:1px solid rgba(201,169,97,0.25);
            padding-bottom:0.6rem;
        }
        .qoc-clauses-grid {
            display:flex;
            flex-direction:column;
            gap:0;
        }
        .qoc-clause-item {
            display:flex;
            align-items:center;
            gap:12px;
            padding:7px 0;
            border-bottom:1px solid rgba(255,255,255,0.07);
        }
        .qoc-clause-item:last-child { border-bottom:none; }
        .qoc-clause-num {
            font-size:0.8rem;
            font-weight:700;
            color:rgba(255,255,255,0.45);
            width:18px;
            flex-shrink:0;
            text-align:right;
        }
        .qoc-clause-code {
            width:52px;
            height:26px;
            border-radius:6px;
            display:flex;
            align-items:center;
            justify-content:center;
            font-size:0.7rem;
            font-weight:900;
            color:white;
            flex-shrink:0;
            letter-spacing:0.5px;
            box-shadow:0 2px 6px rgba(0,0,0,0.3);
        }
        .qoc-clause-title {
            font-size:0.88rem;
            color:rgba(255,255,255,0.88);
            line-height:1.35;
            font-weight:500;
        }

        /* ── RIGHT PANEL ────────────────────────────── */
        .qoc-right {
            flex:1;
            background:#f8fafc;
            display:flex;
            align-items:center;
            justify-content:center;
            padding:3rem 2rem;
        }
        .qoc-form-wrap {
            width:100%;
            max-width:560px;
        }

        /* Card — accent switched to the flag's red */
        .qoc-card {
            background:white;
            border:2px solid #c8102e;
            border-radius:8px;
            padding:2.5rem 2.2rem;
            box-shadow:0 0 0 8px rgba(200,16,46,0.08), 0 20px 60px rgba(0,0,0,0.12);
            position:relative;
        }

        /* Connection badge */
        .qoc-conn {
            position:absolute;
            top:1rem;
            right:1rem;
            display:flex;
            align-items:center;
            gap:6px;
            padding:3px 10px;
            border-radius:20px;
            font-size:0.72rem;
            font-weight:700;
            letter-spacing:0.5px;
            text-transform:uppercase;
        }
        .qoc-conn.online  { background:rgba(34,139,34,0.1); color:#228b22; border:1px solid #228b22; }
        .qoc-conn.offline { background:rgba(178,34,34,0.1);  color:#b22222; border:1px solid #b22222; }
        .qoc-conn-dot { width:7px; height:7px; border-radius:50%; background:currentColor; }

        /* Card header */
        .qoc-card-hdr {
            text-align:center;
            margin-bottom:1.8rem;
            padding-bottom:1.4rem;
            border-bottom:2px solid #c8102e;
        }
        .qoc-card-emblem {
            width:90px;
            height:90px;
            margin:0 auto 1rem;
            border-radius:50%;
            display:flex;
            align-items:center;
            justify-content:center;
            box-shadow:0 0 0 6px rgba(200,16,46,0.2), 0 4px 16px rgba(0,0,0,0.12);
            background:white;
            overflow:hidden;
            padding:6px;
        }
        .qoc-card-title {
            font-family:'Crimson Text',serif;
            font-size:1.8rem;
            font-weight:700;
            color:#0f2944;
            margin-bottom:0.2rem;
            letter-spacing:1px;
        }
        .qoc-card-sub {
            font-size:0.8rem;
            color:#5a6c7d;
            font-weight:600;
            letter-spacing:0.5px;
            text-transform:uppercase;
        }

        /* Form fields */
        .qoc-group { margin-bottom:1.3rem; }
        .qoc-label {
            display:block;
            font-family:'Crimson Text',serif;
            font-size:0.85rem;
            font-weight:700;
            color:#0f2944;
            margin-bottom:0.4rem;
            letter-spacing:1.5px;
            text-transform:uppercase;
        }
        .qoc-input-wrap { position:relative; }
        .qoc-input {
            width:100%;
            padding:0.8rem 1rem;
            font-family:'EB Garamond',serif;
            font-size:1.1rem;
            font-weight:500;
            color:#0f2944;
            background:white;
            border:2px solid #c8102e;
            border-radius:4px;
            transition:all 0.2s;
            outline:none;
        }
        .qoc-input::placeholder { color:#aaa; font-weight:400; }
        .qoc-input:focus { border-color:#0f2944; box-shadow:0 0 0 3px rgba(15,41,68,0.08); }
        .qoc-input.err { border-color:#b22222; background:#fff5f5; }
        .qoc-pw-toggle {
            position:absolute;
            right:0.9rem;
            top:50%;
            transform:translateY(-50%);
            cursor:pointer;
            font-size:1.1rem;
            color:#888;
            user-select:none;
        }
        .qoc-pw-toggle:hover { color:#0f2944; }
        .qoc-field-err {
            color:#b22222;
            font-size:0.78rem;
            font-weight:600;
            margin-top:3px;
        }
        .qoc-caps {
            background:#fff3cd;
            border:1px solid #ffc107;
            color:#856404;
            padding:4px 8px;
            border-radius:4px;
            font-size:0.75rem;
            font-weight:600;
            margin-top:4px;
        }

        /* Remember me */
        .qoc-remember {
            display:flex;
            align-items:center;
            gap:8px;
            margin:1rem 0;
        }
        .qoc-remember input { cursor:pointer; accent-color:#0f2944; }
        .qoc-remember label { font-size:0.85rem; color:#5a6c7d; font-weight:500; cursor:pointer; }

        /* Alerts */
        .qoc-alert-lock {
            background:linear-gradient(135deg,#fff8e1,#ffecb3);
            border:2px solid #ff8f00;
            border-radius:4px;
            padding:0.75rem;
            margin-bottom:1rem;
            color:#e65100;
            font-size:0.85rem;
            font-weight:600;
        }
        .qoc-alert-err {
            background:linear-gradient(135deg,#fff5f5,#ffe8e8);
            border:2px solid #b22222;
            border-radius:4px;
            padding:0.75rem;
            margin-bottom:1rem;
            color:#b22222;
            font-size:0.85rem;
            font-weight:600;
        }
        .qoc-attempts {
            background:#fff3cd;
            border:1px solid #ffc107;
            color:#856404;
            padding:6px 10px;
            border-radius:4px;
            font-size:0.78rem;
            font-weight:600;
            text-align:center;
            margin-bottom:0.8rem;
        }

        /* Submit button */
        .qoc-btn {
            width:100%;
            padding:0.9rem 1.5rem;
            font-family:'Crimson Text',serif;
            font-size:1rem;
            font-weight:700;
            letter-spacing:2px;
            text-transform:uppercase;
            color:white;
            background:linear-gradient(135deg,#0f2944 0%,#1a3a52 50%,#0f2944 100%);
            border:2px solid #c8102e;
            border-radius:4px;
            cursor:pointer;
            transition:all 0.25s;
            box-shadow:0 4px 15px rgba(15,41,68,0.25);
            overflow:hidden;
            position:relative;
        }
        .qoc-btn:hover:not(:disabled) { transform:translateY(-2px); box-shadow:0 6px 20px rgba(15,41,68,0.35); }
        .qoc-btn:disabled { opacity:0.55; cursor:not-allowed; transform:none; }
        .qoc-btn-inner { display:flex; align-items:center; justify-content:center; gap:10px; }
        .qoc-spinner {
            width:16px; height:16px;
            border:3px solid rgba(255,255,255,0.3);
            border-top:3px solid white;
            border-radius:50%;
            animation:spin 0.9s linear infinite;
        }
        @keyframes spin { to { transform:rotate(360deg); } }

        /* Footer */
        .qoc-card-footer {
            margin-top:1.5rem;
            padding-top:1.2rem;
            border-top:1px solid rgba(200,16,46,0.35);
            text-align:center;
            font-size:0.78rem;
            color:#888;
            font-weight:500;
        }

        @media (max-width:768px) {
            .qoc-left { display:none; }
            .qoc-right { padding:2rem 1.5rem; }
        }
    `;

    const mainContent = (
        <>
            <style>{styles}</style>
            <div className="qoc-main">

                {/* ── LEFT PANEL ── */}
                <div className="qoc-left">
                    <div className="qoc-left-bg"/>
                    <div className="qoc-left-overlay"/>
                    <div className="qoc-left-content">
                        {/* Top — MoH branding + national flag */}
                        <div className="qoc-left-top">
                            <div>
                                <div className="qoc-moh-label">Ministry of Health</div>
                                <div className="qoc-kingdom">Kingdom of Eswatini</div>
                            </div>
                            <img
                                src={flagImg}
                                alt="Flag of Eswatini"
                                className="qoc-flag"
                            />
                        </div>
                        {/* Middle — hero */}
                        <div className="qoc-left-main">
                            <div className="qoc-left-title">
                                Quality of<br/><span>Care</span> Standards
                            </div>
                            <div className="qoc-left-subtitle">
                                National Quality Management<br/>Programme - Audit &amp; Analytics Platform
                            </div>
                            
                        </div>

                        {/* Active standards + AYS */}
                        <div className="qoc-clauses">
                            <div className="qoc-clauses-title">Active Assessments</div>
                            <div style={{ display:'flex', flexDirection:'column', gap:8, marginTop:8 }}>
                                {standardsList
                                    
                                    .map((s, i) => (
                                    <div key={s.id} style={{ display:'flex', alignItems:'center', gap:10 }}>
                                        <div style={{ width:10, height:10, borderRadius:'50%', flexShrink:0,
                                            background: s.accent || '#c9a961' }} />
                                        <div style={{ color:'rgba(255,255,255,0.88)', fontSize:15, fontWeight:600 }}>
                                            {s.name}
                                        </div>
                                    </div>
                                ))}
                                {/* AYS always shown */}
                                <div style={{ display:'flex', alignItems:'center', gap:10, marginTop:4,
                                    paddingTop:8, borderTop:'1px solid rgba(255,255,255,0.1)' }}>
                                    <div style={{ width:10, height:10, borderRadius:'50%', flexShrink:0,
                                        background:'#86efac' }} />
                                    <div style={{ color:'rgba(255,255,255,0.88)', fontSize:15, fontWeight:600 }}>
                                        Adolescent & Youth Services Scorecard
                                    </div>
                                    <div style={{ color:'rgba(255,255,255,0.88)', fontSize:15, fontWeight:600 }}>
                                        Custom Assessment and Survey Builder
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── RIGHT PANEL ── */}
                <div className="qoc-right">
                    <div className="qoc-form-wrap">
                        <div className="qoc-card">

                            {/* Connection status */}
                            <div className={`qoc-conn ${connectionStatus}`}>
                                <span className="qoc-conn-dot"/>
                                <span>{connectionStatus === 'online' ? 'Connected' : 'Offline'}</span>
                            </div>

                            {/* Header */}
                            <div className="qoc-card-hdr">
                                <div className="qoc-card-emblem">
                                <img src="assets/img/elogo.png" alt="Eswatini Coat of Arms"
                                    style={{ width:'100%', height:'100%', objectFit:'contain' }}/>
                            </div>
                                <div className="qoc-card-title">Authorized Access</div>
                                <div className="qoc-card-sub">QoC Management System</div>
                            </div>

                            <form onSubmit={login} autoComplete="off">

                                {/* Username */}
                                <div className="qoc-group">
                                    <label className="qoc-label">Username</label>
                                    <div className="qoc-input-wrap">
                                        <input
                                            ref={usernameRef}
                                            type="text"
                                            readOnly
                                            onFocus={e => e.target.removeAttribute('readOnly')}
                                            placeholder="Enter your username"
                                            value={values.username || ''}
                                            onChange={e => handleInputChange('username', e.target.value)}
                                            onKeyDown={e => handleKeyDown(e, 'username')}
                                            className={`qoc-input${validationErrors.username ? ' err' : ''}`}
                                            autoComplete="off"
                                            disabled={isLocked}/>
                                    </div>
                                    {validationErrors.username && (
                                        <div className="qoc-field-err">⚠ {validationErrors.username}</div>
                                    )}
                                </div>

                                {/* Password */}
                                <div className="qoc-group">
                                    <label className="qoc-label">Password</label>
                                    <div className="qoc-input-wrap">
                                        <input
                                            ref={passwordRef}
                                            readOnly
                                            onFocus={e => e.target.removeAttribute('readOnly')}
                                            type={showPassword ? 'text' : 'password'}
                                            placeholder="Enter your password"
                                            value={values.password || ''}
                                            onChange={e => handleInputChange('password', e.target.value)}
                                            onKeyDown={e => handleKeyDown(e, 'password')}
                                            className={`qoc-input${validationErrors.password ? ' err' : ''}`}
                                            autoComplete="new-password"
                                            disabled={isLocked}/>
                                        <div className="qoc-pw-toggle"
                                            onClick={() => setShowPassword(s => !s)}
                                            title={showPassword ? 'Hide' : 'Show'}>
                                            {showPassword ? '👁️' : '👁️‍🗨️'}
                                        </div>
                                    </div>
                                    {validationErrors.password && (
                                        <div className="qoc-field-err">⚠ {validationErrors.password}</div>
                                    )}
                                    {capsLockOn && (
                                        <div className="qoc-caps">⚠ Caps Lock is ON</div>
                                    )}
                                </div>

                                {/* Remember me */}
                                <div className="qoc-remember">
                                    <input type="checkbox" id="qoc-rem"
                                        checked={rememberMe}
                                        onChange={e => setRememberMe(e.target.checked)}
                                        disabled={isLocked}/>
                                    <label htmlFor="qoc-rem">Remember my username</label>
                                </div>

                                {/* Lockout */}
                                {isLocked && (
                                    <div className="qoc-alert-lock">
                                        🔒 Account temporarily locked. Try again in {getRemainingTime()}
                                    </div>
                                )}

                                {/* Error */}
                                {error && !isLocked && (
                                    <div className="qoc-alert-err">⛔ {error}</div>
                                )}

                                {/* Attempts warning */}
                                {loginAttempts > 0 && loginAttempts < 5 && !isLocked && (
                                    <div className="qoc-attempts">
                                        <strong>Security:</strong> {5 - loginAttempts} attempt{5-loginAttempts!==1?'s':''} remaining before lockout
                                    </div>
                                )}

                                {/* Submit */}
                                <button type="submit" className="qoc-btn"
                                    disabled={loading || isLocked || !values.username || !values.password || connectionStatus==='offline'}>
                                    {loading ? (
                                        <div className="qoc-btn-inner">
                                            <div className="qoc-spinner"/>
                                            <span>Authenticating...</span>
                                        </div>
                                    ) : connectionStatus === 'offline' ? (
                                        <div className="qoc-btn-inner"><span>No Connection</span></div>
                                    ) : isLocked ? (
                                        <div className="qoc-btn-inner"><span>🔒 Locked</span></div>
                                    ) : (
                                        <div className="qoc-btn-inner"><span>Sign In</span></div>
                                    )}
                                </button>
                                <div style={{textAlign:'center',marginTop:14}}>
                                    <button type="button" onClick={()=>setShowForgot(true)}
                                        style={{background:'none',border:'none',color:'#60a5fa',fontSize:13,cursor:'pointer',textDecoration:'underline'}}>
                                        Forgot your password?
                                    </button>
                                </div>

                            </form>

                            <div className="qoc-card-footer">
                                For official use only &nbsp;·&nbsp;
                                © {new Date().getFullYear()} Kingdom of Eswatini — Ministry of Health
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </>
    );

    const forgotModal = (
        <Modal
            title="Reset your password"
            open={showForgot}
            onCancel={closeForgotModal}
            footer={null}
        >
            {forgotSent ? (
                <div style={{ textAlign:'center', padding:'12px 0' }}>
                    <div style={{ fontSize:32, marginBottom:8 }}>✅</div>
                    <p style={{ marginBottom:16 }}>
                        If an account exists for <strong>{forgotEmail}</strong>, a password reset link has been sent.
                    </p>
                    <Button type="primary" onClick={closeForgotModal}>Close</Button>
                </div>
            ) : (
                <>
                    <p style={{ color:'#5a6c7d', fontSize:13, marginBottom:12 }}>
                        Enter the email address associated with your account and we'll send you a link to reset your password.
                    </p>
                    <Input
                        type="email"
                        placeholder="you@example.com"
                        value={forgotEmail}
                        onChange={e => setForgotEmail(e.target.value)}
                        onPressEnter={handleForgot}
                        style={{ marginBottom:16 }}
                    />
                    <Button type="primary" block loading={forgotLoading} onClick={handleForgot}>
                        Send Reset Link
                    </Button>
                </>
            )}
        </Modal>
    );

    return (
        <>
            {mainContent}
            {forgotModal}
        </>
    );
}




